import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import { Send } from 'lucide-react';

const EmailModal = ({
    show,
    handleClose,
    invoiceData,
    handleSendEmail,
    isReminder = false
}) => {
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [emailCopy, setEmailCopy] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const formRef = useRef(null);

    useEffect(() => {
        if (invoiceData && show) {
            // Set default email from invoice data
            let contactOverride = invoiceData.contact_override;
            if (typeof contactOverride === 'string') {
                try { contactOverride = JSON.parse(contactOverride); } catch { contactOverride = null; }
            }
            setEmail(invoiceData.delivery_email || contactOverride?.contact_email || invoiceData.email || '');
            setError(null);
            setEmailCopy(false);

            // Set default subject based on whether this is a reminder or regular email
            const invoiceNumber = invoiceData.invoicenumber || '';
            if (isReminder) {
                setSubject(`Påminnelse: Faktura ${invoiceNumber}`);
                setEmailBody(generateReminderTemplate(invoiceData));
            } else {
                setSubject(`Faktura ${invoiceNumber}`);
                setEmailBody(generateEmailTemplate(invoiceData));
            }
        }
    }, [invoiceData, show, isReminder]);

    const generateEmailTemplate = (invoice) => {
        const dueDate = invoice.deadline ? new Date(invoice.deadline).toLocaleDateString('sv-SE') : 'N/A';
        const amount = invoice.totals?.grand_total != null ?
            new Intl.NumberFormat('sv-SE', { style: 'currency', currency: invoice.currency || 'SEK' }).format(invoice.totals.grand_total) :
            'N/A';

        return `Hej ${invoice.customername || ''},

Här kommer faktura ${invoice.invoicenumber || ''}.

Fakturabelopp: ${amount}
Förfallodatum: ${dueDate}

Vänligen betala fakturan enligt betalningsuppgifterna på fakturan senast på förfallodatumet.

Med vänliga hälsningar,
${invoice.company_name || 'Företaget'}`;
    };

    const generateReminderTemplate = (invoice) => {
        const dueDate = invoice.deadline ? new Date(invoice.deadline).toLocaleDateString('sv-SE') : 'N/A';
        const amount = invoice.totals?.grand_total != null ?
            new Intl.NumberFormat('sv-SE', { style: 'currency', currency: invoice.currency || 'SEK' }).format(invoice.totals.grand_total) :
            'N/A';

        return `Hej ${invoice.customername || ''},

Vi vill påminna om vår obetalda faktura ${invoice.invoicenumber || ''} som förföll den ${dueDate}.

Fakturabelopp: ${amount}

Vänligen betala fakturan omgående enligt betalningsuppgifterna på fakturan.

Med vänliga hälsningar,
${invoice.company_name || 'Företaget'}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isLoading || !formRef.current?.reportValidity()) return;
        setIsLoading(true);
        setError(null);

        try {
            await handleSendEmail({
                email,
                subject,
                body: emailBody,
                sendCopy: emailCopy,
                isReminder
            });

            handleClose();
        } catch (err) {
            setError('Ett fel uppstod vid sändning av e-post. Vänligen försök igen.');
        } finally {
            setIsLoading(false);
        }
    };

    const amount = invoiceData?.totals?.grand_total != null
        ? new Intl.NumberFormat('sv-SE', { style: 'currency', currency: invoiceData.currency || 'SEK' }).format(invoiceData.totals.grand_total)
        : null;
    const dueDate = invoiceData?.deadline ? new Date(invoiceData.deadline).toLocaleDateString('sv-SE') : null;
    const summary = [
        invoiceData?.customername || invoiceData?.name,
        amount,
        dueDate ? `förfaller ${dueDate}` : null,
    ].filter(Boolean);

    return (
        <Modal
            show={show}
            onHide={isLoading ? undefined : handleClose}
            size="lg"
            centered
            backdrop={isLoading ? 'static' : true}
            keyboard={!isLoading}
            dialogClassName="inv-email-modal"
        >
            <Modal.Header closeButton={!isLoading}>
                <div>
                    <Modal.Title as="h5" className="inv-email-modal__title">
                        {isReminder ? 'Skicka påminnelse' : 'Skicka faktura'}
                        {invoiceData?.invoicenumber && <span className="inv-email-modal__number">{invoiceData.invoicenumber}</span>}
                    </Modal.Title>
                    {summary.length > 0 && (
                        <div className="inv-email-modal__summary">
                            {summary.map((part, i) => (
                                <React.Fragment key={`${part}-${i}`}>
                                    {i > 0 && <span className="inv-dot">·</span>}
                                    {part}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="inv-note inv-note--error mb-3" role="alert">{error}</div>
                )}

                <form ref={formRef} onSubmit={handleSubmit} noValidate={false}>
                    <div className="inv-grid inv-grid--fields inv-email-modal__grid">
                        <div>
                            <label className="inv-label" htmlFor="invoice-email-recipient">Till</label>
                            <input
                                id="invoice-email-recipient"
                                type="email"
                                className="inv-control"
                                placeholder="kund@exempel.se"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoFocus={!email}
                            />
                        </div>
                        <div>
                            <label className="inv-label" htmlFor="invoice-email-subject">Ämne</label>
                            <input
                                id="invoice-email-subject"
                                type="text"
                                className="inv-control"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="mt-3">
                        <label className="inv-label" htmlFor="invoice-email-message">Meddelande</label>
                        <textarea
                            id="invoice-email-message"
                            className="inv-control inv-email-modal__body"
                            rows={9}
                            value={emailBody}
                            onChange={(e) => setEmailBody(e.target.value)}
                            required
                        />
                        <div className="inv-help mt-1">Fakturan bifogas som PDF.</div>
                    </div>

                    <div className="inv-toggle-row inv-email-modal__copy">
                        <div className="inv-toggle-row__text">Skicka en kopia till mig</div>
                        <Form.Check
                            type="switch"
                            id="invoice-email-copy"
                            aria-label="Skicka en kopia till mig"
                            checked={emailCopy}
                            onChange={(e) => setEmailCopy(e.target.checked)}
                        />
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="inv-btn inv-btn--secondary" onClick={handleClose} disabled={isLoading}>
                    Avbryt
                </button>
                <button type="button" className="inv-btn inv-btn--primary" onClick={handleSubmit} disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            Skickar…
                        </>
                    ) : (
                        <>
                            <Send strokeWidth={2} />
                            {isReminder ? 'Skicka påminnelse' : 'Skicka faktura'}
                        </>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default EmailModal;
